/**
 * useSearchState — single source of truth for the unified search surface
 * (VOY-470 epic, VOY-471 foundational ticket).
 *
 * Pure logic. No UI. Three views of one state object:
 *   - AI parser writes into it (parseAIQuery)
 *   - Classic form edits it directly (setField / setMany)
 *   - Editable pills inline-edit it (setField)
 *
 * Boundary translation lives in this file:
 *   - parseURL accepts the union of /search and /search-plus URL contracts.
 *   - parseSessionStorage handles all four `voySearchData.searchType` variants
 *     produced today by profile/rows/RecentSearchesRow.tsx.
 *   - toURLParams emits the canonical /search-style contract (shareable links).
 *   - toBookingPayload emits the legacy `travelers`/`segments` keys the
 *     existing search-plus results pipeline already consumes.
 */

import { useCallback, useMemo, useReducer } from 'react'
import {
  type AirportRef,
  type Cabin,
  type Leg,
  type Passengers,
  type ProductChip,
  type SearchState,
  type TripType,
  INITIAL_SEARCH_STATE,
} from './types'
import { parseTravelQuery, type ParsedTravelQuery } from './parser'

type Action =
  | { type: 'SET_FIELD'; key: keyof SearchState; value: SearchState[keyof SearchState] }
  | { type: 'SET_MANY'; partial: Partial<SearchState> }
  | { type: 'TOGGLE_CHIP'; chip: ProductChip }
  | { type: 'RESET' }
  | { type: 'REPLACE'; next: SearchState }

function reducer(state: SearchState, action: Action): SearchState {
  switch (action.type) {
    case 'SET_FIELD':
      return { ...state, [action.key]: action.value }
    case 'SET_MANY':
      return mergeState(state, action.partial)
    case 'TOGGLE_CHIP':
      return { ...state, chips: { ...state.chips, [action.chip]: !state.chips[action.chip] } }
    case 'RESET':
      return INITIAL_SEARCH_STATE
    case 'REPLACE':
      return action.next
    default:
      return state
  }
}

/**
 * Deep-merge a partial update onto a complete SearchState. Top-level keys
 * shallow-overwrite, but `passengers` and `chips` deep-merge so a partial
 * `{ passengers: { adults: 2 } }` doesn't wipe `children`/`childrenAges`,
 * and a partial `{ chips: { hotels: false } }` doesn't wipe the other
 * chips. Exported so SmartSearchCard can compose the post-parseAIQuery
 * state synchronously (React's state update would be one tick stale).
 */
export function mergeState(state: SearchState, partial: Partial<SearchState>): SearchState {
  const next: SearchState = { ...state, ...partial }
  if (partial.passengers) {
    next.passengers = { ...state.passengers, ...partial.passengers }
  }
  if (partial.chips) {
    next.chips = { ...state.chips, ...partial.chips }
  }
  return next
}

export interface UseSearchStateApi {
  state: SearchState
  setField: <K extends keyof SearchState>(key: K, value: SearchState[K]) => void
  setMany: (partial: Partial<SearchState>) => void
  toggleChip: (chip: ProductChip) => void
  reset: () => void

  parseURL: (search: string) => void
  parseSessionStorage: () => void
  /**
   * Call the backend AI parser, dispatch the resulting fields, and return
   * the same partial that was applied. The dispatched state-update is async
   * (React doesn't re-render until the next tick), so callers that need the
   * post-parse state synchronously — e.g. `SmartSearchCard.handleSubmit`
   * forwarding the full state to `onSubmit` — should merge the returned
   * partial with the current state themselves rather than reading
   * `api.state` after the await (which would be stale).
   */
  parseAIQuery: (
    query: string,
    options?: { userId?: string; inputMode?: 'chat' | 'web' },
  ) => Promise<Partial<SearchState>>

  toURLParams: () => URLSearchParams
  toBookingPayload: () => Record<string, unknown>
}

export function useSearchState(initial?: Partial<SearchState>): UseSearchStateApi {
  const [state, dispatch] = useReducer(
    reducer,
    initial ? mergeState(INITIAL_SEARCH_STATE, initial) : INITIAL_SEARCH_STATE,
  )

  const setField = useCallback<UseSearchStateApi['setField']>((key, value) => {
    dispatch({ type: 'SET_FIELD', key, value: value as SearchState[keyof SearchState] })
  }, [])

  const setMany = useCallback((partial: Partial<SearchState>) => {
    dispatch({ type: 'SET_MANY', partial })
  }, [])

  const toggleChip = useCallback((chip: ProductChip) => {
    dispatch({ type: 'TOGGLE_CHIP', chip })
  }, [])

  const reset = useCallback(() => {
    dispatch({ type: 'RESET' })
  }, [])

  /**
   * Replace the entire state with whatever is encoded in the URL search
   * string. Intended for first paint on entry to a search route. Any prior
   * in-progress edits (chip toggles, query, inputMode, imageSearch) are
   * dropped — start with INITIAL_SEARCH_STATE and overlay what the URL
   * provides. Don't call this after the user has begun interacting unless
   * you mean to reset.
   */
  const parseURL = useCallback((search: string) => {
    const next = mergeState(INITIAL_SEARCH_STATE, fromURL(search))
    dispatch({ type: 'REPLACE', next })
  }, [])

  /**
   * Replace the entire state with what's encoded in sessionStorage's
   * `voySearchData` key, then one-shot consume the key. Same REPLACE
   * semantics as `parseURL` — any prior in-progress edits are dropped.
   * No-op when the key is absent or malformed.
   */
  const parseSessionStorage = useCallback(() => {
    const partial = fromSessionStorage()
    if (!partial) return
    const next = mergeState(INITIAL_SEARCH_STATE, partial)
    dispatch({ type: 'REPLACE', next })
  }, [])

  const parseAIQuery = useCallback(
    async (
      query: string,
      options: { userId?: string; inputMode?: 'chat' | 'web' } = {},
    ) => {
      const parsed = await parseTravelQuery(query, options)
      const partial = fromParsedTravelQuery(query, parsed)
      dispatch({ type: 'SET_MANY', partial })
      return partial
    },
    [],
  )

  return useMemo<UseSearchStateApi>(
    () => ({
      state,
      setField,
      setMany,
      toggleChip,
      reset,
      parseURL,
      parseSessionStorage,
      parseAIQuery,
      toURLParams: () => toURLParams(state),
      toBookingPayload: () => toBookingPayload(state),
    }),
    [state, setField, setMany, toggleChip, reset, parseURL, parseSessionStorage, parseAIQuery],
  )
}

/* ------------------------------------------------------------------ */
/* URL contract                                                        */
/* ------------------------------------------------------------------ */

/**
 * Parse the union of two URL contracts currently in use:
 *
 *   /search (RecentSearchesRow flight path):
 *     q, mode, autoSearch, origin, destination, originLabel, destinationLabel,
 *     departureDate, returnDate, adults, children, source, savedSearchId
 *
 *   /search-plus (legacy direct deep links):
 *     q, mode, autoSearch, preview, tripType,
 *     from, to, departure, return, adults, children, class, segments (JSON)
 *
 * Either form may be present. Canonical (/search) keys win when both exist.
 */
export function fromURL(search: string): Partial<SearchState> {
  const params = new URLSearchParams(search)
  const partial: Partial<SearchState> = {}

  // AI / mode
  const q = params.get('q')
  if (q) partial.query = q
  const mode = params.get('mode')
  if (mode === 'ai' || mode === 'classic' || mode === 'voymytrip') partial.mode = mode
  const autoSearch = params.get('autoSearch')
  if (autoSearch === 'true') partial.autoSearch = true

  // Origin / destination — accept canonical or legacy
  const origin = params.get('origin') ?? params.get('from') ?? undefined
  const originLabel = params.get('originLabel') ?? undefined
  if (origin) partial.from = labelOrUndefined(origin, originLabel)

  const destination = params.get('destination') ?? params.get('to') ?? undefined
  const destinationLabel = params.get('destinationLabel') ?? undefined
  if (destination) partial.to = labelOrUndefined(destination, destinationLabel)

  // Dates — accept canonical or legacy; truncate ISO timestamps to YYYY-MM-DD
  const departureDate = truncateDate(params.get('departureDate') ?? params.get('departure'))
  if (departureDate) partial.departureDate = departureDate
  const returnDate = truncateDate(params.get('returnDate') ?? params.get('return'))
  if (returnDate) partial.returnDate = returnDate

  // Passengers
  const adults = numericOrUndefined(params.get('adults'))
  const children = numericOrUndefined(params.get('children'))
  if (adults !== undefined || children !== undefined) {
    partial.passengers = {
      adults: adults ?? INITIAL_SEARCH_STATE.passengers.adults,
      children: children ?? INITIAL_SEARCH_STATE.passengers.children,
    }
  }

  // Cabin (accepts legacy `class` and `cabinClass`; normalizes 'Premium Economy' → 'premium-economy')
  applyCabin(partial, params.get('class') ?? params.get('cabinClass'))

  // Trip type (translates legacy 'hotel' to chips-only-hotels)
  applyTripType(partial, params.get('tripType'))

  // Multi-city segments (legacy JSON string of legs)
  const segments = params.get('segments')
  if (segments) {
    const legs = parseSegmentsJSON(segments)
    if (legs.length > 0) {
      partial.legs = legs
      if (!partial.tripType) partial.tripType = 'multi-city'
    }
  }

  // Provenance
  const source = params.get('source')
  if (source) partial.source = source
  const savedSearchId = params.get('savedSearchId')
  if (savedSearchId) partial.savedSearchId = savedSearchId

  return partial
}

/**
 * Emit URL params for the unified search surface. The first eleven keys
 * (`q`, `mode`, `autoSearch`, `origin`/`destination`/`originLabel`/
 * `destinationLabel`, `departureDate`/`returnDate`, `adults`/`children`,
 * `source`/`savedSearchId`) are the canonical /search contract. Fields
 * outside that set — `class` (cabin), `tripType`, `segments` (multi-city
 * legs as JSON) — keep their legacy /search-plus names because the
 * canonical contract doesn't define them. fromURL parses both contracts,
 * so roundtrip through this function is stable.
 */
export function toURLParams(state: SearchState): URLSearchParams {
  const params = new URLSearchParams()

  if (state.query) params.set('q', state.query)
  // Always emit mode so AI/Classic intent survives the roundtrip.
  params.set('mode', state.mode)
  if (state.autoSearch) params.set('autoSearch', 'true')

  if (state.from?.code) params.set('origin', state.from.code)
  if (state.from?.label) params.set('originLabel', state.from.label)
  if (state.to?.code) params.set('destination', state.to.code)
  if (state.to?.label) params.set('destinationLabel', state.to.label)

  if (state.departureDate) params.set('departureDate', state.departureDate)
  if (state.returnDate) params.set('returnDate', state.returnDate)

  // Always emit pax (roundtrip determinism — both endpoints expect counts).
  params.set('adults', String(state.passengers.adults))
  if (state.passengers.children > 0) {
    params.set('children', String(state.passengers.children))
  }

  if (state.cabin && state.cabin !== INITIAL_SEARCH_STATE.cabin) {
    params.set('class', state.cabin)
  }
  if (state.tripType && state.tripType !== INITIAL_SEARCH_STATE.tripType) {
    params.set('tripType', state.tripType)
  }

  if (state.legs && state.legs.length > 0) {
    params.set('segments', JSON.stringify(state.legs.map(legToSegment)))
  }

  if (state.source) params.set('source', state.source)
  if (state.savedSearchId) params.set('savedSearchId', state.savedSearchId)

  return params
}

/* ------------------------------------------------------------------ */
/* sessionStorage contract                                             */
/* ------------------------------------------------------------------ */

interface VoySearchDataBase {
  searchType?: 'ai-direct' | 'ai-confirmed' | 'classic' | 'saved-search'
  source?: string
  savedSearchId?: string
  autoSearch?: boolean
  originalQuery?: string
  processedParams?: Record<string, unknown>
  confirmedParams?: Record<string, unknown>
}

/**
 * Read `voySearchData` from sessionStorage and one-shot consume it.
 * Handles all four searchType variants produced today by
 * profile/rows/RecentSearchesRow.tsx. Clears the key after a successful read.
 */
export function fromSessionStorage(): Partial<SearchState> | null {
  if (typeof window === 'undefined') return null
  const raw = window.sessionStorage.getItem('voySearchData')
  if (!raw) return null

  let data: VoySearchDataBase
  try {
    data = JSON.parse(raw) as VoySearchDataBase
  } catch {
    window.sessionStorage.removeItem('voySearchData')
    return null
  }

  // Pick the payload bucket. `confirmedParams` wins for 'ai-confirmed' and
  // 'saved-search'; `processedParams` for 'ai-direct' and 'classic'.
  const params = data.confirmedParams ?? data.processedParams ?? {}

  const partial: Partial<SearchState> = normalizeRecordToState(params)

  if (data.originalQuery) partial.query = String(data.originalQuery)
  if (data.searchType === 'classic') partial.mode = 'classic'
  else if (data.searchType) partial.mode = 'ai'
  if (typeof data.autoSearch === 'boolean') partial.autoSearch = data.autoSearch
  if (data.source) partial.source = data.source
  if (data.savedSearchId) partial.savedSearchId = String(data.savedSearchId)

  // One-shot consumption — match existing search-plus.tsx behavior.
  window.sessionStorage.removeItem('voySearchData')
  return partial
}

/* ------------------------------------------------------------------ */
/* AI parser response normalization                                    */
/* ------------------------------------------------------------------ */

export function fromParsedTravelQuery(
  query: string,
  parsed: ParsedTravelQuery,
): Partial<SearchState> {
  const partial: Partial<SearchState> = normalizeRecordToState(
    parsed as unknown as Record<string, unknown>,
  )
  partial.query = query
  return partial
}

/* ------------------------------------------------------------------ */
/* Booking payload (legacy keys expected by the results pipeline)      */
/* ------------------------------------------------------------------ */

export function toBookingPayload(state: SearchState): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    tripType: state.tripType,
    cabin: state.cabin,
    travelers: {
      adults: state.passengers.adults,
      children: state.passengers.children,
      ...(state.passengers.childrenAges?.length
        ? { childrenAges: state.passengers.childrenAges }
        : {}),
    },
  }

  if (state.from?.code) {
    payload.origin = state.from.code
    if (state.from.label) payload.originName = state.from.label
  }
  if (state.to?.code) {
    payload.destination = state.to.code
    if (state.to.label) payload.destinationName = state.to.label
  }
  if (state.departureDate) payload.departureDate = state.departureDate
  if (state.returnDate) payload.returnDate = state.returnDate
  if (state.legs && state.legs.length > 0) {
    payload.segments = state.legs.map(legToSegment)
  }
  if (state.query) payload.originalQuery = state.query
  if (state.source) payload.source = state.source
  if (state.savedSearchId) payload.savedSearchId = state.savedSearchId
  if (state.imageSearch) payload.imageSearch = state.imageSearch

  return payload
}

/* ------------------------------------------------------------------ */
/* Internal helpers                                                    */
/* ------------------------------------------------------------------ */

/**
 * Normalize a loose record (sessionStorage payload, AI parser response, etc.)
 * into a Partial<SearchState>. Accepts both canonical and legacy keys.
 */
function normalizeRecordToState(record: Record<string, unknown>): Partial<SearchState> {
  const partial: Partial<SearchState> = {}

  const origin = strOrUndefined(record.origin) ?? strOrUndefined(record.from)
  const originLabel = strOrUndefined(record.originName) ?? strOrUndefined(record.originLabel)
  if (origin) partial.from = labelOrUndefined(origin, originLabel)

  const destination = strOrUndefined(record.destination) ?? strOrUndefined(record.to)
  const destinationLabel =
    strOrUndefined(record.destinationName) ?? strOrUndefined(record.destinationLabel)
  if (destination) partial.to = labelOrUndefined(destination, destinationLabel)

  const departureDate = truncateDate(record.departureDate ?? record.checkin)
  if (departureDate) partial.departureDate = departureDate
  const returnDate = truncateDate(record.returnDate ?? record.checkout)
  if (returnDate) partial.returnDate = returnDate

  const travelers = record.travelers as
    | { adults?: unknown; children?: unknown; childrenAges?: unknown }
    | undefined
  const passengersIn = record.passengers as
    | { adults?: unknown; children?: unknown; childrenAges?: unknown }
    | undefined
  const passengers = normalizePassengers(travelers, passengersIn)
  if (passengers) partial.passengers = passengers

  // Cabin: accept `cabin` or `cabinClass`. Legacy producers emit
  // `cabinClass: cabinClass.toLowerCase()` which yields strings such as
  // 'premium economy' (with a space) — applyCabin normalizes to hyphenated.
  applyCabin(partial, record.cabin ?? record.cabinClass)

  // Trip type: applyTripType also translates the legacy 'hotel' tripType
  // (still produced by RecentSearchesRow.tsx for hotel saved-searches) into
  // chips={hotels-only} since the internal TripType union drops 'hotel'.
  applyTripType(partial, record.tripType)

  const segments = record.segments
  if (Array.isArray(segments) && segments.length > 0) {
    const legs = segments
      .map((seg) => segmentToLeg(seg as Record<string, unknown>))
      .filter((leg): leg is Leg => leg !== null)
    if (legs.length > 0) {
      partial.legs = legs
      if (!partial.tripType) partial.tripType = 'multi-city'
    }
  }

  return partial
}

/**
 * Set `target.cabin` from a loose input, accepting the canonical and legacy
 * forms (`'premium-economy'`, `'Premium Economy'`, `'PREMIUM_ECONOMY'`).
 * Silently drops invalid values.
 */
function applyCabin(target: Partial<SearchState>, raw: unknown): void {
  const s = strOrUndefined(raw)
  if (!s) return
  const normalized = s.toLowerCase().replace(/[\s_]+/g, '-')
  if (isCabin(normalized)) target.cabin = normalized
}

/**
 * Set `target.tripType` from a loose input. Two special cases:
 *   - `'hotel'` (legacy producer in RecentSearchesRow.tsx hotel saved-search
 *     path) translates to chips={flights:false, hotels:true, itinerary:false}
 *     and leaves tripType at its default — the internal TripType union no
 *     longer includes 'hotel' because chips.hotels carries that signal.
 *   - Invalid strings are silently dropped.
 */
function applyTripType(target: Partial<SearchState>, raw: unknown): void {
  const s = strOrUndefined(raw)
  if (!s) return
  const lower = s.toLowerCase()
  if (lower === 'hotel') {
    target.chips = { flights: false, hotels: true, itinerary: false }
    return
  }
  if (isTripType(lower)) target.tripType = lower
}

/**
 * Coerce a loose value into a `YYYY-MM-DD` string. Accepts either the
 * canonical date-only form or an ISO 8601 timestamp (which legacy producers
 * emit via `.toISOString()`); returns undefined for anything that
 * isn't a non-empty string.
 */
function truncateDate(v: unknown): string | undefined {
  const s = strOrUndefined(v)
  if (!s) return undefined
  return s.split('T')[0]
}

function normalizePassengers(
  travelers?: { adults?: unknown; children?: unknown; childrenAges?: unknown },
  passengers?: { adults?: unknown; children?: unknown; childrenAges?: unknown },
): Passengers | undefined {
  const source = travelers ?? passengers
  if (!source) return undefined
  const adults = numericOrUndefined(source.adults)
  const children = numericOrUndefined(source.children)
  if (adults === undefined && children === undefined) return undefined
  const out: Passengers = {
    adults: adults ?? INITIAL_SEARCH_STATE.passengers.adults,
    children: children ?? INITIAL_SEARCH_STATE.passengers.children,
  }
  const ages = source.childrenAges
  if (Array.isArray(ages)) {
    const numericAges = ages
      .map((a) => numericOrUndefined(a))
      .filter((n): n is number => n !== undefined)
    if (numericAges.length > 0) out.childrenAges = numericAges
  }
  return out
}

function labelOrUndefined(code: string, label?: string): AirportRef {
  return label ? { code, label } : { code }
}

function isCabin(value: string): value is Cabin {
  return value === 'economy' || value === 'premium-economy' || value === 'business' || value === 'first'
}

function isTripType(value: string): value is TripType {
  return value === 'one-way' || value === 'round-trip' || value === 'multi-city'
}

function strOrUndefined(v: unknown): string | undefined {
  if (typeof v === 'string' && v.length > 0) return v
  return undefined
}

function numericOrUndefined(v: unknown): number | undefined {
  if (v === null || v === undefined || v === '') return undefined
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) ? n : undefined
}

function parseSegmentsJSON(raw: string): Leg[] {
  try {
    const arr = JSON.parse(raw) as unknown
    if (!Array.isArray(arr)) return []
    return arr
      .map((seg) => segmentToLeg(seg as Record<string, unknown>))
      .filter((leg): leg is Leg => leg !== null)
  } catch {
    return []
  }
}

function segmentToLeg(seg: Record<string, unknown>): Leg | null {
  if (!seg || typeof seg !== 'object') return null
  const fromCode = strOrUndefined(seg.from) ?? strOrUndefined(seg.origin)
  const toCode = strOrUndefined(seg.to) ?? strOrUndefined(seg.destination)
  const fromLabel = strOrUndefined(seg.fromLabel) ?? strOrUndefined(seg.originName)
  const toLabel = strOrUndefined(seg.toLabel) ?? strOrUndefined(seg.destinationName)
  const date = strOrUndefined(seg.date) ?? strOrUndefined(seg.departureDate)
  const leg: Leg = {}
  if (fromCode) leg.from = labelOrUndefined(fromCode, fromLabel)
  if (toCode) leg.to = labelOrUndefined(toCode, toLabel)
  if (date) leg.date = date
  return leg.from || leg.to || leg.date ? leg : null
}

function legToSegment(leg: Leg): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  if (leg.from?.code) {
    out.origin = leg.from.code
    if (leg.from.label) out.originName = leg.from.label
  }
  if (leg.to?.code) {
    out.destination = leg.to.code
    if (leg.to.label) out.destinationName = leg.to.label
  }
  if (leg.date) out.date = leg.date
  return out
}

// Re-export type surface so consumers can `import { SearchState, ... } from '@/lib/search/useSearchState'`.
export type {
  AirportRef,
  Cabin,
  ImageSearchAttachment,
  InputMode,
  Leg,
  Passengers,
  ProductChip,
  SearchMode,
  SearchState,
  TripType,
} from './types'
export { INITIAL_SEARCH_STATE } from './types'
